import { EventProgramItem } from "@/types/EventProgramItem";

/**
 * THE order of an event's programme items — one comparator for every surface.
 *
 * The programme is displayed CHRONOLOGICALLY. `sortIndex` survives as a silent
 * TIEBREAK only: it still orders two blocks that start at the same instant, and
 * it keeps legacy rows deterministic, but it never decides the visible order on
 * its own and the UI never writes it any more (the drag-reorder lane was
 * removed with this cut).
 *
 * Why this module exists: the same collection used to be read with four
 * different sort keys — the website bake by `sortIndex`, the cockpit and
 * main-backend by `sortIndex, startTime`, the attendee app and the
 * `{{sessionList}}` mails by `startTime, sortIndex`, and the check-in pick
 * sheet by nothing at all. One event therefore had several published truths.
 * Everything in main-frontend now routes through here; the server-side twins
 * sort `{startTime, sortIndex, _id}`, which is the same order.
 *
 * Home is `lib/utils` on purpose: `lib/website-builder/eventBinding.ts` (the
 * bake) must not gain a react-query dependency, and `lib/query/hooks/
 * useEventProgram.ts` must not pull the 2000-line bake module into every hook
 * consumer. Both import this instead.
 */

/**
 * Start instant in epoch ms; missing/unparsable → `+Infinity`, so undated items
 * land LAST.
 *
 * NEVER compare the ISO strings themselves: stored values mix `…:00Z` (server),
 * `…:00.000Z` (client `toISOString()`) and offset forms like `…+02:00`, and
 * lexicographic order gets those wrong (`09:00+02:00` IS earlier than `08:00Z`).
 * `?? 0` would be worse still — it hoists undated rows to 1970 and puts them
 * first.
 */
export function programStartMs(item: EventProgramItem): number {
  const ms = item.startTime ? new Date(item.startTime).getTime() : Number.NaN;
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * Total order: startTime → sortIndex → createdAt → programItemId.
 *
 * The `programItemId` tail is what makes it TOTAL, and that matters beyond
 * tidiness: without it two rows equal in every other key keep whatever order
 * Mongo happened to return, a refetch can flip them, and the website bake's
 * reconciler then sees an order diff, rewrites the section and raises
 * `bakeDirty` — "unsaved changes" appearing out of nothing.
 *
 * `sortIndex` is read tolerantly (`?? 0`): the type says it is mandatory and
 * both create paths stamp it, but legacy and directly-inserted rows exist, and
 * one `undefined` would turn the subtraction into `NaN` and silently neuter the
 * entire sort.
 */
export function compareProgramItems(
  a: EventProgramItem,
  b: EventProgramItem,
): number {
  return (
    programStartMs(a) - programStartMs(b) ||
    (a.sortIndex ?? 0) - (b.sortIndex ?? 0) ||
    (a.createdAt ?? "").localeCompare(b.createdAt ?? "") ||
    (a.programItemId ?? "").localeCompare(b.programItemId ?? "")
  );
}

/** Sorted COPY in display order. Pure — the input array is never touched. */
export function sortProgramItemsChronologically(
  items: readonly EventProgramItem[],
): EventProgramItem[] {
  return [...items].sort(compareProgramItems);
}
